import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { t } from 'i18next'

import AutoComplete from 'src/components/shared/ui/AutoComplete'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import ButtonSecondary from 'src/components/shared/ui/ButtonSecondary'
import LexnetDialog from 'src/components/shared/ui/LexnetDialog'
import Loader from 'src/components/shared/ui/Loader/Loader'
import useFetching from 'src/hooks/useFetching'
import { Document, Expedient } from 'src/interfaces/lexon'
import { documentsByEntityType, searchDocuments } from 'src/services/Lexon/DocumentService'
import TableDocuments from '../TableDocuments'

type Props = {
  title: string
  isOpened: boolean
  close: () => void
  prev: () => void
  selectedDocument: Document | null
  setSelectedDocument: (row: Document[]) => void
  inputSearch?: string
  expedientSelected?: Expedient | null
  extAllowed?: string
  onSelection: () => void
}

export default function DialogDocument(props: Props): JSX.Element {
  const {
    title,
    isOpened,
    close,
    prev,
    selectedDocument,
    setSelectedDocument,
    inputSearch = '',
    expedientSelected,
    onSelection,
    extAllowed = '.pdf'
  } = props

  const [documentData, setDocumentData] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const documents = useFetching<Document[]>({
    key: ['lexon-documents'],
    fetchFn: () => searchDocuments(inputSearch, extAllowed),
    enabled: false
  })

  const expedientDocument = useFetching<Document[]>({
    key: ['lexon-expedient-documents'],
    fetchFn: async () => {
      if (expedientSelected) {
        return documentsByEntityType(expedientSelected.idExpedient, extAllowed)
      }
      return new Promise(resolve => resolve([]))
    },
    enabled: false
  })

  useEffect(() => {
    async function fetch() {
      setIsLoading(true)
      if (expedientSelected && isOpened) {
        await expedientDocument.refetch()
        setIsLoading(false)
        return
      }

      if (inputSearch && isOpened) {
        await documents.refetch()
        setIsLoading(false)
      }
    }
    fetch()
    // eslint-disable-next-line
  }, [inputSearch, expedientSelected, isOpened])

  useEffect(() => {
    if (expedientSelected && expedientDocument.data) {
      setDocumentData(expedientDocument.data)
    }
  }, [expedientSelected, expedientDocument.data])

  useEffect(() => {
    if (inputSearch && documents.data) {
      setDocumentData(documents.data)
    }
  }, [inputSearch, documents.data])

  function back() {
    prev()
    setSelectedDocument([])
  }

  return (
    <>
      <LexnetDialog
        title={title}
        isOpen={isOpened}
        close={close}
        maxWidth="lg"
        buttons={
          <>
            <ButtonPrimaryOutlined onClick={close}>{t('actions.cancel')}</ButtonPrimaryOutlined>
            <ButtonPrimaryOutlined onClick={back}>{t('actions.previous')}</ButtonPrimaryOutlined>
            <ButtonSecondary onClick={onSelection} disabled={!selectedDocument}>
              {t('actions.attach')}
            </ButtonSecondary>
          </>
        }
      >
        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
            <Loader />
          </Box>
        ) : (
          <>
            {expedientSelected && (
              <Box sx={{ marginBottom: '16px' }}>
                <AutoComplete
                  label={t('form.legend.expediente')}
                  id="expedients-dialog-document"
                  itemName="description"
                  itemKey="id"
                  onChange={() => null}
                  value={{
                    id: expedientSelected.idOwnFile,
                    description: `${expedientSelected.idOwnFile} - ${expedientSelected.description}`
                  }}
                  items={[
                    {
                      id: expedientSelected.idOwnFile,
                      description: `${expedientSelected.idOwnFile} - ${expedientSelected.description}`
                    }
                  ]}
                  disabled
                />
              </Box>
            )}
            <TableDocuments
              isExpedientType={!!expedientSelected}
              search={expedientSelected ? inputSearch : ''}
              data={documentData}
              setSelectedDocument={setSelectedDocument}
              selectedDocument={selectedDocument}
            />
          </>
        )}
      </LexnetDialog>
    </>
  )
}
